import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Brush as BrushIcon,
  Add as AddIcon,
  TextFields as TextIcon,
  CropSquare as RectIcon,
  RadioButtonUnchecked as CircleIcon,
  Remove as LineIcon,
  RotateRight as RotateIcon,
  PanTool as SelectIcon,
} from '@mui/icons-material';
import { fabric } from 'fabric';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';
import { GamifyToast } from '../../common/CustomToasts';

interface FabricCanvasWithElements extends fabric.Canvas {
  upperCanvasEl: HTMLCanvasElement;
  lowerCanvasEl: HTMLCanvasElement;
}

interface PhotoAnnotatorProps {
  open: boolean;
  onClose: () => void;
  photo: {
    imageName: string;
    tags: string[];
    description: string;
    fileId?: string;
    type?: string;
  } | null;
  dealId: string;
  onSave?: () => void;
}

// Colors matching mobile editor
const COLORS = [
  '#FFFFFF',
  '#14191A',
  '#4275FD',
  '#1DD1A1',
  '#FECA43',
  '#FF6A00',
  '#DB2408',
  '#FA52EE',
  '#8A52FA',
];

export default function PhotoAnnotator({
  open,
  onClose,
  photo,
  onSave,
}: PhotoAnnotatorProps) {
  const { theme } = useTheme();
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const calcOffsetRafRef = useRef<number | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [newTag, setNewTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentColor, setCurrentColor] = useState('#FF6A00');
  const [isDrawMode, setIsDrawMode] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [historyStack, setHistoryStack] = useState<string[]>([]);
  const [editSessionId, setEditSessionId] = useState(0);

  // Text editing state
  const [textInputOpen, setTextInputOpen] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');
  const editingTextRef = useRef<fabric.Text | null>(null);

  const scheduleCalcOffset = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    if (calcOffsetRafRef.current != null) {
      cancelAnimationFrame(calcOffsetRafRef.current);
    }

    calcOffsetRafRef.current = requestAnimationFrame(() => {
      calcOffsetRafRef.current = null;
      canvas.calcOffset();
      canvas.requestRenderAll();
    });
  }, []);

  const applyInteractionMode = useCallback(
    (canvas: fabric.Canvas, drawMode: boolean) => {
      canvas.isDrawingMode = drawMode;
      canvas.freeDrawingCursor = 'crosshair';
      canvas.hoverCursor = drawMode ? 'crosshair' : 'move';
      canvas.moveCursor = 'move';
      canvas.defaultCursor = drawMode ? 'crosshair' : 'default';

      if (!drawMode) {
        canvas.selection = true;
        canvas.getObjects().forEach((obj: any) => {
          obj.selectable = true;
          obj.evented = true;
          obj.hasControls = true;
          obj.hasBorders = true;
          obj.lockMovementX = false;
          obj.lockMovementY = false;
          obj.lockScalingX = false;
          obj.lockScalingY = false;
          obj.lockRotation = false;
          obj.lockScalingFlip = false;
          obj.setCoords();
        });
      } else {
        canvas.selection = false;
        canvas.discardActiveObject();
      }

      canvas.requestRenderAll();
    },
    []
  );

  // Reset state when dialog opens/closes
  useEffect(() => {
    let cancelled = false;

    if (photo && open) {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setTags(photo.tags || []);
      setDescription(photo.description || '');
      setHasChanges(false);
      setIsEditing(false);
      setImageLoaded(false);
      setHistoryStack([]);
      setIsDrawMode(true);
      setImageUrl(null);

      const loadImage = async () => {
        try {
          if (photo.type?.startsWith('image/')) {
            const result = await Storage.get(photo.imageName, {
              level: 'public',
              download: true,
            });
            if (cancelled) return;
            const blob = (result as any).Body as Blob;
            const reader = new FileReader();
            reader.onloadend = () => {
              if (cancelled) return;
              setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(blob);
          } else {
            const url = await Storage.get(photo.imageName, { level: 'public' });
            if (cancelled) return;
            setImageUrl(url as string);
          }
        } catch (err) {
          console.error('Error loading image:', err);
          const url = await Storage.get(photo.imageName, { level: 'public' });
          if (cancelled) return;
          setImageUrl(url as string);
        }
      };
      loadImage();
    }

    if (!open) {
      // Cleanup when closing
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      if (canvasHostRef.current) {
        canvasHostRef.current.innerHTML = '';
      }
      setIsEditing(false);
      setIsDrawMode(true);
      setImageLoaded(false);
      setHistoryStack([]);
      setImageUrl(null);
    }
    return () => {
      cancelled = true;
    };
  }, [photo, open]);

  useEffect(() => {
    if (!isEditing) return;

    const container = containerRef.current;
    const onScrollOrResize = () => scheduleCalcOffset();

    container?.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, { passive: true });

    return () => {
      container?.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize);

      if (calcOffsetRafRef.current != null) {
        cancelAnimationFrame(calcOffsetRafRef.current);
        calcOffsetRafRef.current = null;
      }
    };
  }, [isEditing, scheduleCalcOffset]);

  // Initialize canvas when entering edit mode
  useEffect(() => {
    if (
      !isEditing ||
      !canvasHostRef.current ||
      !imageUrl ||
      !containerRef.current
    )
      return;

    const container = containerRef.current;
    const maxW = container.clientWidth - 48;
    const maxH = window.innerHeight - 400;
    const hostEl = canvasHostRef.current;

    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;

      hostEl.innerHTML = '';
      const canvasEl = document.createElement('canvas');
      hostEl.appendChild(canvasEl);

      // Create canvas
      const canvas = new fabric.Canvas(canvasEl, {
        width: w,
        height: h,
        isDrawingMode: true,
        selection: true,
        preserveObjectStacking: true,
      });
      fabricCanvasRef.current = canvas;
      // Prevent browser panning/scrolling gestures from stealing drag events
      const fabricCanvas = canvas as FabricCanvasWithElements;
      fabricCanvas.upperCanvasEl.style.touchAction = 'none';
      fabricCanvas.upperCanvasEl.style.userSelect = 'none';
      fabricCanvas.upperCanvasEl.style.webkitUserSelect = 'none';
      fabricCanvas.upperCanvasEl.style.pointerEvents = 'auto';
      fabricCanvas.lowerCanvasEl.style.pointerEvents = 'none';
      fabricCanvas.upperCanvasEl.tabIndex = 0;
      fabricCanvas.upperCanvasEl.focus({ preventScroll: true });
      applyInteractionMode(canvas, isDrawMode);

      // Set brush
      canvas.freeDrawingBrush.color = currentColor;
      canvas.freeDrawingBrush.width = 4;

      // Load background
      fabric.Image.fromURL(imageUrl, (fabricImg) => {
        fabricImg.scaleToWidth(w);
        canvas.setBackgroundImage(fabricImg, () => {
          canvas.renderAll();
          setImageLoaded(true);
          saveHistory();
          scheduleCalcOffset();
        });
      });

      // Track changes
      canvas.on('object:added', () => setHasChanges(true));
      canvas.on('object:modified', () => {
        setHasChanges(true);
        saveHistory();
      });

      // Double-click to edit text objects
      canvas.on('mouse:dblclick', (opt) => {
        const target = opt.target;
        if (target && target.type === 'text') {
          editingTextRef.current = target as fabric.Text;
          setTextInputValue((target as fabric.Text).text || '');
          setTextInputOpen(true);
        }
      });

      scheduleCalcOffset();
    };
    img.src = imageUrl;

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      hostEl.innerHTML = '';
    };
  }, [editSessionId, isEditing, imageUrl]);

  const startEditing = useCallback(() => {
    setImageLoaded(false);
    setIsDrawMode(true);
    setIsEditing(true);
    setEditSessionId((prev) => prev + 1);
  }, []);

  const stopEditing = useCallback(() => {
    setIsEditing(false);
    setImageLoaded(false);
  }, []);

  // Update brush color
  useEffect(() => {
    if (fabricCanvasRef.current?.freeDrawingBrush) {
      fabricCanvasRef.current.freeDrawingBrush.color = currentColor;
    }
  }, [currentColor]);

  // Toggle draw mode and ensure objects are selectable when not drawing
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    applyInteractionMode(canvas, isDrawMode);
    scheduleCalcOffset();
  }, [applyInteractionMode, isDrawMode, scheduleCalcOffset]);

  const saveHistory = useCallback(() => {
    if (fabricCanvasRef.current) {
      setHistoryStack((prev) => [
        ...prev,
        JSON.stringify(fabricCanvasRef.current!.toJSON()),
      ]);
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (historyStack.length > 1 && fabricCanvasRef.current) {
      const newStack = historyStack.slice(0, -1);
      setHistoryStack(newStack);
      const canvas = fabricCanvasRef.current;
      canvas.loadFromJSON(newStack[newStack.length - 1], () => {
        canvas.renderAll();
        applyInteractionMode(canvas, isDrawMode);
        scheduleCalcOffset();
      });
    }
  }, [applyInteractionMode, historyStack, isDrawMode, scheduleCalcOffset]);

  const addShape = useCallback(
    (type: 'text' | 'rect' | 'circle' | 'line') => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      // Exit draw mode so shapes can be selected, dragged, resized
      canvas.isDrawingMode = false;
      setIsDrawMode(false);

      const shapeDefaults = {
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false,
        lockMovementX: false,
        lockMovementY: false,
      };

      let obj: fabric.Object;
      const center = {
        left: canvas.width! / 2 - 50,
        top: canvas.height! / 2 - 50,
      };

      switch (type) {
        case 'text': {
          // Open text input dialog - text will be created in handleTextSubmit
          editingTextRef.current = null;
          setTextInputValue('');
          setTextInputOpen(true);
          return;
        }
        case 'rect':
          obj = new fabric.Rect({
            ...center,
            width: 100,
            height: 80,
            fill: 'transparent',
            stroke: currentColor,
            strokeWidth: 3,
            ...shapeDefaults,
          });
          break;
        case 'circle':
          obj = new fabric.Circle({
            ...center,
            radius: 50,
            fill: 'transparent',
            stroke: currentColor,
            strokeWidth: 3,
            ...shapeDefaults,
          });
          break;
        case 'line':
          obj = new fabric.Line(
            [center.left, center.top, center.left + 100, center.top],
            {
              stroke: currentColor,
              strokeWidth: 3,
              ...shapeDefaults,
            }
          );
          break;
        default:
          return;
      }

      canvas.add(obj);
      canvas.setActiveObject(obj);
      obj.setCoords();
      canvas.requestRenderAll();
      saveHistory();
    },
    [currentColor, saveHistory]
  );

  const handleTextSubmit = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !textInputValue.trim()) {
      setTextInputOpen(false);
      setTextInputValue('');
      editingTextRef.current = null;
      return;
    }

    if (editingTextRef.current) {
      // Editing existing text
      editingTextRef.current.set('text', textInputValue);
      canvas.renderAll();
      setHasChanges(true);
      saveHistory();
    } else {
      // Creating new text
      const center = {
        left: canvas.width! / 2,
        top: canvas.height! / 2,
      };

      const textObj = new fabric.Text(textInputValue, {
        ...center,
        fontSize: 28,
        fill: currentColor,
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        originX: 'center',
        originY: 'center',
      });

      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      textObj.setCoords();
      canvas.requestRenderAll();
      setHasChanges(true);
      saveHistory();
    }

    setTextInputOpen(false);
    setTextInputValue('');
    editingTextRef.current = null;
  }, [currentColor, saveHistory, textInputValue]);

  const handleTextCancel = useCallback(() => {
    setTextInputOpen(false);
    setTextInputValue('');
    editingTextRef.current = null;
  }, []);

  const handleRotate = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const bg = canvas.backgroundImage as fabric.Image;
    if (bg) {
      bg.rotate((bg.angle || 0) + 90);
      canvas.renderAll();
      setHasChanges(true);
      saveHistory();
    }
  }, [saveHistory]);

  const handleAddTag = useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed]);
      setNewTag('');
      setHasChanges(true);
    }
  }, [newTag, tags]);

  const handleRemoveTag = useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!photo?.fileId || !hasChanges) {
      stopEditing();
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      let newUrl = photo.imageName;

      // If edited, export canvas and upload
      if (isEditing && fabricCanvasRef.current) {
        const dataUrl = fabricCanvasRef.current.toDataURL({
          format: 'png',
          quality: 1,
        });
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const fileName = `edited_${Date.now()}_${photo.imageName
          .split('/')
          .pop()}`;
        await Storage.put(fileName, blob, {
          contentType: 'image/png',
          level: 'public',
        });
        newUrl = fileName;
      }

      await API.graphql(
        graphqlOperation(mutations.updateDealFile, {
          input: {
            id: photo.fileId,
            description,
            tags,
            ...(newUrl !== photo.imageName && { url: newUrl }),
          },
        })
      );

      GamifyToast.success('Photo updated');
      onSave?.();
      stopEditing();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      GamifyToast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [
    photo,
    hasChanges,
    isEditing,
    description,
    tags,
    onSave,
    onClose,
    stopEditing,
  ]);

  const handleClose = useCallback(() => {
    if (hasChanges && !window.confirm('Discard changes?')) return;
    stopEditing();
    onClose();
  }, [hasChanges, onClose, stopEditing]);

  const isVideo = photo?.type?.startsWith('video/');
  const isPdf = photo?.type === 'application/pdf';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.background1,
          color: theme.text,
          maxHeight: '90vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${theme.stroke}`,
        }}
      >
        <Typography variant="h6">Edit Photo</Typography>
        <IconButton onClick={handleClose} sx={{ color: theme.text }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent ref={containerRef} sx={{ p: 3 }}>
        {isEditing ? (
          <Box>
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                p: 1.5,
                backgroundColor: theme.background2,
                borderRadius: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {/* Colors */}
              {COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setCurrentColor(c)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: c,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border:
                      currentColor === c
                        ? `3px solid ${theme.primary}`
                        : '1px solid #666',
                  }}
                />
              ))}

              <Box
                sx={{
                  mx: 1,
                  borderLeft: `1px solid ${theme.stroke}`,
                  height: 28,
                }}
              />

              {/* Draw/Select toggle */}
              <Tooltip title={isDrawMode ? 'Drawing Mode' : 'Select Mode'}>
                <IconButton
                  onClick={() => setIsDrawMode(!isDrawMode)}
                  sx={{
                    backgroundColor: isDrawMode
                      ? theme.primary
                      : theme.background3,
                    color: isDrawMode ? '#fff' : theme.text,
                  }}
                >
                  {isDrawMode ? <BrushIcon /> : <SelectIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Add Text">
                <IconButton
                  onClick={() => addShape('text')}
                  sx={{ color: theme.text }}
                >
                  <TextIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Rectangle">
                <IconButton
                  onClick={() => addShape('rect')}
                  sx={{ color: theme.text }}
                >
                  <RectIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Circle">
                <IconButton
                  onClick={() => addShape('circle')}
                  sx={{ color: theme.text }}
                >
                  <CircleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Line">
                <IconButton
                  onClick={() => addShape('line')}
                  sx={{ color: theme.text }}
                >
                  <LineIcon />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  mx: 1,
                  borderLeft: `1px solid ${theme.stroke}`,
                  height: 28,
                }}
              />

              <Tooltip title="Rotate">
                <IconButton onClick={handleRotate} sx={{ color: theme.text }}>
                  <RotateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    onClick={handleUndo}
                    disabled={historyStack.length <= 1}
                    sx={{ color: theme.text }}
                  >
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Canvas */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: theme.background2,
                borderRadius: 1,
                p: 2,
                minHeight: 200,
              }}
            >
              {!imageLoaded && <CircularProgress />}
              <Box
                ref={canvasHostRef}
                sx={{ display: imageLoaded ? 'block' : 'none' }}
              />
            </Box>

            {/* Text Input Dialog */}
            <Dialog
              open={textInputOpen}
              onClose={handleTextCancel}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  backgroundColor: theme.background1,
                  color: theme.text,
                },
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {editingTextRef.current ? 'Edit Text' : 'Add Text'}
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  value={textInputValue}
                  onChange={(e) => setTextInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleTextSubmit();
                    } else if (e.key === 'Escape') {
                      handleTextCancel();
                    }
                  }}
                  placeholder="Enter your text..."
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.background2,
                      color: theme.text,
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Button
                    onClick={handleTextCancel}
                    sx={{ color: theme.textLight }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleTextSubmit}
                    disabled={!textInputValue.trim()}
                    sx={{ backgroundColor: theme.primary }}
                  >
                    {editingTextRef.current ? 'Update' : 'Add'}
                  </Button>
                </Box>
              </Box>
            </Dialog>
          </Box>
        ) : (
          <Box>
            {/* Preview */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
                position: 'relative',
              }}
            >
              {imageUrl ? (
                <>
                  {isVideo ? (
                    <video
                      src={imageUrl}
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: 400,
                        borderRadius: 8,
                      }}
                    />
                  ) : isPdf ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.background2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography>PDF Preview</Typography>
                    </Box>
                  ) : (
                    <img
                      src={imageUrl}
                      alt="Photo"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 400,
                        borderRadius: 8,
                      }}
                    />
                  )}
                  {!isVideo && !isPdf && (
                    <Button
                      variant="contained"
                      startIcon={<BrushIcon />}
                      onClick={startEditing}
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        backgroundColor: theme.primary,
                      }}
                    >
                      Edit Image
                    </Button>
                  )}
                </>
              ) : (
                <CircularProgress />
              )}
            </Box>

            {/* Tags */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: theme.textLight }}
              >
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    sx={{
                      backgroundColor: theme.background2,
                      color: theme.text,
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && (e.preventDefault(), handleAddTag())
                  }
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.background2,
                      color: theme.text,
                    },
                  }}
                />
                <IconButton
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                  sx={{ color: theme.primary }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Description */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: theme.textLight }}
              >
                Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Add description..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.background2,
                    color: theme.text,
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.stroke}` }}>
        <Button onClick={handleClose} sx={{ color: theme.textLight }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ backgroundColor: theme.primary }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
